import React, { useState } from "react";
import { img } from "./blocks";

// Phone preview of a resolved service page, styled with the app's design tokens.
// It shows content and order; pixel details live in the app.

const C = {
  ink: "#1B1726", muted: "#6E6880", brand: "#7F58E6", brandStrong: "#5A36C4", tint: "#F3EEFE", line: "#ECE7F3",
  alt: "#F8F6FB", success: "#1C7F52", warning: "#A8620A", warningTint: "#FFF3E0", danger: "#C2264B", star: "#E0A100",
};
const inr = (n) => (n == null ? "" : `₹${Number(n).toLocaleString("en-IN")}`);
const parse = (s) => { try { return JSON.parse(s || "{}"); } catch (e) { return {}; } };
const compact = (n) => (n >= 100000 ? `${Math.floor(n / 100000)} lakh+` : n >= 1000 ? `${Math.floor(n / 1000)}K+` : n >= 100 ? `${Math.floor(n / 10) * 10}+` : `${n}`);

// Same as the app: price-bucket categories are hidden, and the product is not repeated when the name has it.
const eyebrow = (s) => {
  const category = /^under\s*\d/i.test(s.categoryName || "") ? null : s.categoryName;
  const product = s.isPackage ? null : s.productName;
  return [category, product].filter((t) => t && !s.name.toLowerCase().includes(t.toLowerCase())).join(" · ");
};

const Gap = () => <div style={{ height: 6, background: C.alt }} />;
const Title = ({ children, sub }) => (
  <div className="px-3 pb-2">
    <div style={{ color: C.ink }} className="text-[13.5px] font-bold">{children}</div>
    {sub && <div style={{ color: C.muted }} className="text-[10px]">{sub}</div>}
  </div>
);
const Photo = ({ src, ratio = "1/1", radius = 8, className = "" }) => (
  <div className={`overflow-hidden bg-[#F3EEFE] ${className}`} style={{ aspectRatio: ratio, borderRadius: radius }}>
    {src && <img src={img(src)} alt="" className="w-full h-full object-cover" />}
  </div>
);
const Add = () => <span className="text-[9.5px] font-bold border rounded px-2 py-0.5 bg-white" style={{ color: C.brandStrong, borderColor: "#DCCFFB" }}>ADD</span>;
const Meta = ({ s }) => (
  <div style={{ color: C.muted }} className="text-[9px]">
    {s.ratingCount ? <><b style={{ color: C.success }}>★ {s.ratingAvg}</b> ({s.ratingCount}){s.durationMinutes ? " · " : ""}</> : null}
    {s.durationMinutes ? `${s.durationMinutes} min` : ""}
  </div>
);
const ICON = { check: "✓", leaf: "🌿", sparkle: "✨", shield: "🛡", clock: "⏱", drop: "💧", sun: "☀", star: "★", user: "🙂", gift: "🎁", home: "🏠", heart: "♥", brush: "🖌", spa: "🪷", ac: "❄", ban: "⛔", info: "ℹ", thermo: "🌡", hand: "✋" };

const Block = ({ b, currentId }) => {
  const c = parse(b.config);
  switch (b.type) {
    case "MEDIA":
      return <div className="px-3 py-3"><Photo src={c.images?.[0]} ratio={(c.aspect || "4:3").replace(":", "/")} />{c.images?.length > 1 && <div className="text-[9px] mt-1" style={{ color: C.muted }}>+{c.images.length - 1} more photos</div>}</div>;
    case "HIGHLIGHTS":
      if (c.style === "BANNER") {
        return (
          <div className="px-3 py-3">
            <div className="rounded-xl overflow-hidden" style={{ background: c.background || C.tint }}>
              <div className="p-3">
                {c.headline && <div className="text-[15px] font-extrabold leading-tight" style={{ color: c.accent || C.brandStrong }}>{c.headline}</div>}
                {c.body && <div className="text-[10px] mt-1" style={{ color: C.ink }}>{c.body}</div>}
                {(c.items || []).map((i, k) => <div key={k} className="text-[10.5px] mt-1" style={{ color: c.accent || C.brandStrong }}>{ICON[i.icon] || "✓"} {i.text}</div>)}
              </div>
              {c.image && <Photo src={c.image} ratio="16/9" radius={0} />}
            </div>
          </div>
        );
      }
      return (
        <div className="py-3">
          {(b.title || c.headline) && <Title sub={b.subtitle || c.body}>{b.title || c.headline}</Title>}
          <div className={`px-3 ${c.style === "CHIPS" ? "flex flex-wrap gap-1" : ""}`}>
            {(c.items || []).map((i, k) => (c.style === "CHIPS"
              ? <span key={k} className="text-[9.5px] rounded-full px-2 py-0.5" style={{ background: C.tint }}>{ICON[i.icon]} {i.text}</span>
              : <div key={k} className="flex gap-2 items-center text-[10.5px] py-1" style={{ color: C.ink }}><span className="w-5 h-5 rounded-full flex items-center justify-center text-[10px]" style={{ background: C.tint }}>{ICON[i.icon] || "✓"}</span>{i.text}</div>))}
          </div>
        </div>
      );
    case "OPTIONS":
      return (
        <div className="py-3">
          <Title sub={b.subtitle}>{b.title || "Choose your option"}</Title>
          <div className="flex gap-2 overflow-x-auto px-3">
            {b.services.map((s) => {
              const sel = s.id === (c.currentServiceId || currentId);
              return (
                <div key={s.id} className="flex-none w-[104px] rounded-lg p-1.5 flex flex-col gap-0.5" style={{ border: `${sel ? 1.5 : 1}px solid ${sel ? C.brand : C.line}`, background: sel ? C.tint : "#fff" }}>
                  <Photo src={s.image} radius={6} />
                  <div className="text-[10px] font-semibold leading-tight line-clamp-2" style={{ color: C.ink }}>{c.labelBy === "PRODUCT" && s.productName ? s.productName : s.name}</div>
                  <Meta s={s} />
                  <div className="flex items-center justify-between"><b className="text-[10.5px]">{inr(s.campaignPrice || s.price)}</b>{sel ? <span className="text-[9px] text-white rounded px-1.5" style={{ background: C.brand }}>✓</span> : <Add />}</div>
                </div>
              );
            })}
          </div>
        </div>
      );
    case "INCLUDES":
      return (
        <div className="py-3">
          <Title sub={c.selectionCount ? `Pick any ${c.selectionCount} of these ${b.services.length}` : `${b.services.length} services in one visit`}>{b.title || "What's included"}</Title>
          {b.services.map((s) => <div key={s.id} className="flex gap-2 items-center px-3 py-1"><Photo src={s.image} className="w-8 flex-none" radius={6} /><span className="text-[10.5px] font-semibold">{s.name}</span></div>)}
        </div>
      );
    case "STEPS":
      return (
        <div className="py-3">
          <Title sub={b.subtitle}>{b.title || "How it works"}{c.style === "CAROUSEL" ? " · swipe cards" : ""}</Title>
          <div className="px-3">
            {(c.steps || []).map((s, i) => (
              <div key={i} className="flex gap-2 pb-2">
                <span className="w-5 h-5 rounded-full flex-none flex items-center justify-center text-[9.5px] font-bold" style={{ background: C.tint, color: C.brandStrong }}>{i + 1}</span>
                <div className="flex-1">
                  {s.title && <div className="text-[10.5px] font-semibold">{s.title}</div>}
                  {s.body && <div className="text-[10px]" style={{ color: s.title ? C.muted : C.ink }}>{s.body}</div>}
                  {s.images?.length > 0 && <div className="flex gap-1 mt-1">{s.images.map((u) => <Photo key={u} src={u} className="flex-1" ratio={s.images.length === 1 ? "16/10" : "1/1"} />)}</div>}
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    case "BEFORE_AFTER":
      return (
        <div className="py-3">
          <Title>{b.title || "See the difference"}</Title>
          <div className="flex gap-1 px-3"><Photo src={c.before} ratio="3/4" className="flex-1" /><Photo src={c.after} ratio="3/4" className="flex-1" /></div>
          {c.caption && <div className="px-3 text-[9px] mt-1" style={{ color: C.muted }}>{c.caption}</div>}
        </div>
      );
    case "PRODUCTS":
      return (
        <div className="py-3">
          <Title>{b.title || "Products we use"}</Title>
          <div className="px-3 flex flex-wrap gap-2">
            {(c.items || []).map((p, i) => (p.imageUrl
              ? <div key={i} className="w-14 text-center"><Photo src={p.imageUrl} /><div className="text-[8.5px] mt-0.5">{p.name}</div></div>
              : <div key={i} className="w-full text-[10.5px]" style={{ color: C.ink }}><span style={{ color: C.success }}>✓</span> {p.name}</div>))}
          </div>
        </div>
      );
    case "BULLETS": {
      const items = c.items || [];
      if (c.style === "NOTE") {
        return (
          <div className="px-3 py-3">
            <div className="rounded-lg p-2.5" style={{ background: C.warningTint }}>
              <div className="text-[12.5px] font-bold" style={{ color: C.warning }}>ⓘ {b.title || "Please note"}</div>
              {items.map((i, k) => <div key={k} className="text-[10px] mt-1" style={{ color: C.ink }}>• {i.text}</div>)}
            </div>
          </div>
        );
      }
      const mark = c.style === "TIPS" ? "✨" : c.style === "PLAIN" ? "•" : "✓";
      return (
        <div className="py-3">
          {b.title && <Title>{b.title}</Title>}
          {items.slice(0, 5).map((i, k) => <div key={k} className="px-3 text-[10.5px] py-0.5" style={{ color: C.ink }}><span style={{ color: c.style === "CHECK" || !c.style ? C.success : C.brandStrong }}>{ICON[i.icon] && c.style !== "PLAIN" ? ICON[i.icon] : mark}</span> {i.text}</div>)}
          {items.length > 5 && <div className="px-3 text-[10px] font-bold" style={{ color: C.brandStrong }}>Show {items.length - 4} more</div>}
        </div>
      );
    }
    case "FAQ":
      return <FaqPreview b={b} c={c} />;
    case "INFO_SHEET":
      return (
        <div className="px-3 py-3">
          <div className="rounded-lg px-3 py-2.5 flex justify-between text-[11px] font-semibold" style={{ background: C.alt }}>💡 {c.label}<span>›</span></div>
          <div className="text-[9px] mt-1" style={{ color: C.muted }}>Opens a sheet: {c.sheetTitle || c.label} · {(c.items || []).length} cards</div>
        </div>
      );
    case "TRUST": {
      const t = b.trust;
      return (
        <div className="px-3 py-3">
          <div className="rounded-xl overflow-hidden" style={{ background: C.tint }}>
            {c.image && <Photo src={c.image} ratio="2/1" radius={0} />}
            <div className="p-3">
              <div className="text-[13px] font-bold">{c.headline || b.title || "The Barbera promise"}</div>
              {t && (
                <div className="flex gap-3 my-1.5 text-[9px]" style={{ color: C.muted }}>
                  {t.ratingAvg && <div><b className="text-[12px]" style={{ color: C.brandStrong }}>{t.ratingAvg}★</b><br />avg rating</div>}
                  {t.completedServices >= 100 && <div><b className="text-[12px]" style={{ color: C.brandStrong }}>{compact(t.completedServices)}</b><br />services done</div>}
                  {t.customers >= 100 && <div><b className="text-[12px]" style={{ color: C.brandStrong }}>{compact(t.customers)}</b><br />customers</div>}
                </div>
              )}
              {(c.points || []).map((p, k) => <div key={k} className="text-[10px] py-0.5" style={{ color: C.ink }}>{ICON[p.icon] || "✓"} {p.text}</div>)}
            </div>
          </div>
        </div>
      );
    }
    case "REVIEWS": {
      const s = b.ratingSummary;
      const max = s ? Math.max(...s.distribution, 1) : 1;
      return (
        <div className="py-3">
          <Title>{b.title || "Ratings & reviews"}</Title>
          {s && (
            <div className="px-3 flex gap-3 items-center">
              <div><div className="text-[20px] font-extrabold">{s.avg}<span className="text-[10px] font-normal" style={{ color: C.muted }}>/5</span></div><div className="text-[9px]" style={{ color: C.muted }}>{s.count} ratings</div></div>
              <div className="flex-1">
                {s.distribution.map((n, i) => (
                  <div key={i} className="flex items-center gap-1 text-[8.5px]" style={{ color: C.muted }}>
                    {5 - i}★<div className="flex-1 h-1 rounded" style={{ background: C.alt }}><div className="h-1 rounded" style={{ width: `${(n / max) * 100}%`, background: i < 2 ? C.success : C.muted }} /></div>{n}
                  </div>
                ))}
              </div>
            </div>
          )}
          {b.reviews.length > 0 && (
            <div className="flex gap-2 overflow-x-auto px-3 mt-2">
              {b.reviews.map((r) => (
                <div key={r.id} className="flex-none w-[180px] border rounded-lg p-2 text-[9.5px]" style={{ borderColor: C.line }}>
                  <div className="flex justify-between"><b>{r.authorName}</b><span style={{ color: C.star }}>{"★".repeat(r.rating)}</span></div>
                  <div className="line-clamp-4 mt-1" style={{ color: C.ink }}>{r.text}</div>
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }
    case "ALSO_ADD":
      return (
        <div className="py-3">
          <Title>{b.title || "Frequently added together"}</Title>
          <div className="flex gap-2 overflow-x-auto px-3">
            {b.services.map((s) => (
              <div key={s.id} className="flex-none w-[90px] flex flex-col gap-0.5">
                <Photo src={s.image} />
                <div className="text-[9.5px] font-semibold line-clamp-2">{s.name}</div>
                <b className="text-[10px]">{inr(s.campaignPrice || s.price)}</b>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
};

const FaqPreview = ({ b, c }) => {
  const [open, setOpen] = useState(0);
  return (
    <div className="py-3">
      <Title>{b.title || "FAQs"}</Title>
      {(c.items || []).map((i, k) => (
        <div key={k} className="px-3 border-b" style={{ borderColor: C.line }}>
          <button type="button" onClick={() => setOpen(open === k ? -1 : k)} className="w-full flex justify-between py-1.5 text-[10.5px] text-left font-medium">{i.q}<span>{open === k ? "▴" : "▾"}</span></button>
          {open === k && <div className="text-[10px] pb-1.5" style={{ color: C.muted }}>{i.a}</div>}
        </div>
      ))}
    </div>
  );
};

const MATCHED = {
  SERVICE: "this page, assigned to the service",
  CATEGORY: "this page, through its category",
  DEFAULT: "this page, as the tab default",
  AUTO: "the automatic page (built from the service's details)",
  PREVIEW: "this page (unsaved changes included)",
};

const PagePreview = ({ detail, loading, error }) => {
  if (error) return <div className="text-sm text-red-600 p-4">{error}</div>;
  if (!detail) return <div className="text-sm text-gray-500 p-4">{loading ? "Building preview…" : "Pick a service to preview."}</div>;
  const s = detail.service;
  const blocks = detail.blocks || [];
  const hero = blocks[0]?.type === "MEDIA" ? parse(blocks[0].config) : null;
  const heroImage = hero ? hero.images?.[0] : s.image;
  const rest = hero ? blocks.slice(1) : blocks;
  return (
    <div>
      <p className="text-xs text-gray-500 mb-2">
        Shows {MATCHED[detail.matchedBy] || detail.pageName}{loading ? " · updating…" : ""}
      </p>
      <div className="mx-auto w-[300px] rounded-[28px] border-[6px] border-gray-900 overflow-hidden bg-white shadow-lg">
        <div className="h-[560px] overflow-y-auto" style={{ fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif", color: C.ink }}>
          {heroImage && <Photo src={heroImage} ratio={(hero?.aspect || "4:3").replace(":", "/")} radius={0} />}
          <div className="px-3 pt-3 pb-3 -mt-3 relative bg-white rounded-t-2xl">
            {eyebrow(s) && <div className="text-[9px] font-bold tracking-wide uppercase" style={{ color: C.brandStrong }}>{eyebrow(s)}</div>}
            <div className="flex justify-between gap-2 mt-0.5"><div className="text-[17px] font-extrabold leading-tight">{s.name}</div><Add /></div>
            <Meta s={s} />
            <div className="text-[13px] font-bold mt-1">{inr(s.campaignPrice || s.price)}{s.campaignPrice ? <s className="ml-1 text-[10px] font-normal" style={{ color: C.muted }}>{inr(s.price)}</s> : null}</div>
            {s.bookedLast30Days ? <span className="text-[9px] font-semibold rounded-full px-2 py-0.5 mt-1 inline-block" style={{ color: C.danger, background: "#FBE8ED" }}>🔥 {s.bookedLast30Days}+ booked this month</span> : null}
            {detail.note && <div className="text-[9.5px] rounded-md px-2 py-1.5 mt-2" style={{ background: C.alt, color: C.muted }}>ⓘ {detail.note}</div>}
          </div>
          {rest.map((b) => <div key={b.id}><Gap /><Block b={b} currentId={s.id} /></div>)}
          {!rest.length && <div className="p-4 text-[11px]" style={{ color: C.muted }}>No blocks have content for this service.</div>}
        </div>
      </div>
    </div>
  );
};

export default PagePreview;
